import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import CloseIcon from '../../../components/svg/CloseIcon';
import ArrowDown from '../../../components/svg/ArrowDown';
import {
  AvatarMember,
  getAvatarColorClass,
  getDisplayName,
  getInitials,
} from '../../../helpers/AvatarHelper';
import './TeamMemberMultiSelect.scss';

const matchesSearch = (member: AvatarMember, search: string): boolean => {
  const query = search.trim().toLowerCase();
  if (!query) return true;
  return (
    getDisplayName(member).toLowerCase().includes(query) ||
    (member.email ?? '').toLowerCase().includes(query)
  );
};

interface TeamMemberMultiSelectProps {
  members: AvatarMember[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}

const TeamMemberMultiSelect = (props: TeamMemberMultiSelectProps): ReactElement => {
  const { members, selectedValues, onChange, placeholder = 'Add...' } = props;

  const [displayed, setDisplayed] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const selectedMembers = useMemo(
    () =>
      members.filter((member) =>
        selectedValues.includes(String(member.organizationUserId)),
      ),
    [members, selectedValues],
  );

  const filteredMembers = useMemo(
    () => members.filter((member) => matchesSearch(member, search)),
    [members, search],
  );

  const allFilteredSelected =
    filteredMembers.length > 0 &&
    filteredMembers.every((member) =>
      selectedValues.includes(String(member.organizationUserId)),
    );

  const toggleValue = (organizationUserId: number): void => {
    const value = String(organizationUserId);
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };

  const removeValue = (organizationUserId: number): void => {
    const value = String(organizationUserId);
    onChange(selectedValues.filter((v) => v !== value));
  };

  const toggleSelectAll = (): void => {
    const filteredIds = filteredMembers.map((member) =>
      String(member.organizationUserId),
    );
    if (allFilteredSelected) {
      onChange(selectedValues.filter((v) => !filteredIds.includes(v)));
    } else {
      const additions = filteredIds.filter((value) => !selectedValues.includes(value));
      onChange([...selectedValues, ...additions]);
    }
  };

  const handleClickOutside = useCallback((e: MouseEvent): void => {
    if (ref.current && ref.current.contains(e.target as Element)) {
      return;
    }
    setDisplayed(false);
    setSearch('');
  }, []);

  useEffect(() => {
    if (displayed) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [displayed, handleClickOutside]);

  return (
    <div className="team-member-multi-select" ref={ref}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div className="team-member-input" onClick={() => setDisplayed(true)}>
        {selectedMembers.map((member) => (
          <span className="tmms-chip" key={member.organizationUserId}>
            <span
              className={`tmms-avatar ${getAvatarColorClass(
                member.organizationUserId,
              )}`}
            >
              {getInitials(member)}
            </span>
            <span className="tmms-chip-label">{getDisplayName(member)}</span>
            <CloseIcon
              className="tmms-chip-remove"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                removeValue(member.organizationUserId);
              }}
            />
          </span>
        ))}

        <input
          className="tmms-search-input"
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setDisplayed(true)}
          placeholder={selectedMembers.length === 0 ? placeholder : ''}
          type="text"
          value={search}
        />

        <ArrowDown className="tmms-caret" />
      </div>

      {displayed && (
        <div className="team-member-dropdown-panel">
          <div className="tmms-dropdown-header">
            <span className="tmms-section-title">Team</span>
            <button className="tmms-select-all" onClick={toggleSelectAll} type="button">
              {allFilteredSelected ? 'Deselect all' : 'Select all'}
            </button>
          </div>

          <div className="tmms-dropdown-list">
            {filteredMembers.map((member) => {
              const checked = selectedValues.includes(
                String(member.organizationUserId),
              );
              return (
                <label className="tmms-dropdown-item" key={member.organizationUserId}>
                  <input
                    checked={checked}
                    onChange={() => toggleValue(member.organizationUserId)}
                    type="checkbox"
                  />
                  <span
                    className={`tmms-avatar ${getAvatarColorClass(
                      member.organizationUserId,
                    )}`}
                  >
                    {getInitials(member)}
                  </span>
                  <span className="tmms-member-info">
                    <span className="tmms-member-name">{getDisplayName(member)}</span>
                    {member.email && (
                      <span className="tmms-member-email">{member.email}</span>
                    )}
                  </span>
                </label>
              );
            })}

            {filteredMembers.length === 0 && (
              <div className="tmms-empty">No team members found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamMemberMultiSelect;
